// Core utility functions for the TerraMatrix frontend

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";
import { AuthTokensSchema, normalizeAuthTokens } from "@/schemas/auth";
import { OrgContextSchema } from "@/schemas/storage";
import {
  clearInMemoryAuthTokens,
  getInMemoryAuthTokens,
} from "@/stores/auth/token-memory";
import type { User } from "@/types";
import { z } from "zod";

const FORBIDDEN_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const PLATFORM_ADMIN_ENTITLEMENT_SNAPSHOT_KEY =
  "__terramatrixPlatformAdminEntitlementSnapshot";
const DEFAULT_PLATFORM_ADMIN_ENTITLEMENT_SNAPSHOT: PlatformAdminEntitlementSnapshot = {
  userId: null,
  platformAdminEntitlement: false,
};

interface PlatformAdminEntitlementSnapshot {
  userId: string | null;
  platformAdminEntitlement: boolean;
}

function isPlatformAdminEntitlementSnapshot(
  value: unknown,
): value is PlatformAdminEntitlementSnapshot {
  return (
    isRecord(value) &&
    (value.userId === null || typeof value.userId === "string") &&
    typeof value.platformAdminEntitlement === "boolean"
  );
}

function readPlatformAdminEntitlementSnapshot(): PlatformAdminEntitlementSnapshot {
  const snapshot = Reflect.get(globalThis, PLATFORM_ADMIN_ENTITLEMENT_SNAPSHOT_KEY);
  return isPlatformAdminEntitlementSnapshot(snapshot)
    ? snapshot
    : DEFAULT_PLATFORM_ADMIN_ENTITLEMENT_SNAPSHOT;
}

export function setPlatformAdminEntitlementSnapshot(
  snapshot: PlatformAdminEntitlementSnapshot,
): void {
  Reflect.set(globalThis, PLATFORM_ADMIN_ENTITLEMENT_SNAPSHOT_KEY, snapshot);
}

// CSS class utility for combining Tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Debounce function for performance optimization
export function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number,
  immediate?: boolean,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };

    const callNow = immediate && !timeout;
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);

    if (callNow) func(...args);
  };
}

// Throttle function for performance optimization
export function throttle<T extends (...args: never[]) => unknown>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Deep clone utility
export function deepClone<T>(obj: T): T {
  const cloned = structuredClone(obj);
  sanitizeObjectGraph(cloned);
  return cloned;
}

function sanitizeObjectGraph(value: unknown): void {
  if (Array.isArray(value)) {
    for (const item of value) {
      sanitizeObjectGraph(item);
    }
    return;
  }

  if (!isRecord(value)) {
    return;
  }

  for (const key of Object.keys(value)) {
    if (FORBIDDEN_OBJECT_KEYS.has(key)) {
      delete value[key];
      continue;
    }
    sanitizeObjectGraph(value[key]);
  }
}

function mergeRecords(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): void {
  for (const [key, sourceValue] of Object.entries(source)) {
    if (FORBIDDEN_OBJECT_KEYS.has(key)) {
      continue;
    }

    if (isRecord(sourceValue)) {
      const existingValue = target[key];
      if (isRecord(existingValue)) {
        mergeRecords(existingValue, sourceValue);
        continue;
      }

      const nestedTarget: Record<string, unknown> = {};
      target[key] = nestedTarget;
      mergeRecords(nestedTarget, sourceValue);
      continue;
    }

    target[key] = sourceValue;
  }
}

// Deep merge utility
export function deepMerge<T extends Record<string, unknown>>(
  target: T,
  ...sources: Partial<T>[]
): T {
  for (const source of sources) {
    if (isRecord(source)) {
      mergeRecords(target, source);
    }
  }

  return target;
}

// Object type checking
export function isRecord(item: unknown): item is Record<string, unknown> {
  return item !== null && typeof item === "object" && !Array.isArray(item);
}

// Format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Format duration
export function formatDuration(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

// Generate unique ID using UUID v4 for globally unique identifiers
export function generateId(prefix?: string): string {
  const uuid = uuidv4();
  return prefix ? `${prefix}_${uuid}` : uuid;
}

// Generate UUID v4
export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Capitalize first letter
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Convert to title case
export function toTitleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}

/**
 * Convert field names to human-readable labels
 * Handles snake_case, camelCase, and kebab-case
 * Examples:
 *   "water_depth" → "Water Depth"
 *   "waterDepth" → "Water Depth"
 *   "water-depth" → "Water Depth"
 *   "pH_level" → "PH Level"
 */
export function humanizeFieldName(fieldName: string): string {
  if (!fieldName) return '';

  return fieldName
    // Insert space before capital letters (camelCase → camel Case)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Replace underscores and hyphens with spaces
    .replace(/[_-]/g, ' ')
    // Capitalize first letter of each word
    .replace(/\b\w/g, c => c.toUpperCase())
    // Clean up multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
}

// Convert camelCase to kebab-case
export function camelToKebab(str: string): string {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2").toLowerCase();
}

// Convert kebab-case to camelCase
export function kebabToCamel(str: string): string {
  return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
}

// Truncate text
export function truncateText(
  text: string,
  maxLength: number,
  suffix: string = "...",
): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
}

// Get file extension
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
}

// Check if string is valid email
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Check if string is valid URL
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Parse JSON safely
export function safeParseJSON(json: string, defaultValue: unknown): unknown {
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed;
  } catch {
    return defaultValue;
  }
}

const StoredUserSchema = z.object({
  email: z.string().optional(),
}).passthrough();

const AUTH_TOKEN_STORAGE_KEY = "terramatrix_tokens";

export type StorageValidator<T> = (value: unknown) => value is T;

function validateStoredValue<T>(
  key: string,
  value: unknown | null,
  defaultValue: T,
  validator?: StorageValidator<T>,
): T {
  if (value === null) {
    return defaultValue;
  }

  if (validator) {
    return validator(value) ? value : defaultValue;
  }

  return matchesDefaultStorageShape(key, value, defaultValue)
    ? value
    : defaultValue;
}

function matchesDefaultStorageShape<T>(
  key: string,
  value: unknown,
  defaultValue: T,
): value is T {
  if (defaultValue === null || defaultValue === undefined) {
    if (key === AUTH_TOKEN_STORAGE_KEY) {
      return isStoredAuthTokens(value);
    }

    if (key === "terramatrix:org-context") {
      return isStoredOrgContext(value);
    }

    if (key === "terramatrix_user") {
      return StoredUserSchema.safeParse(value).success;
    }

    return value === defaultValue;
  }

  if (Array.isArray(defaultValue)) {
    return Array.isArray(value);
  }

  switch (typeof defaultValue) {
    case "boolean":
      return typeof value === "boolean";
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "string":
      return typeof value === "string";
    case "object":
      return isRecord(value);
    default:
      return false;
  }
}

export function isStoredAuthTokens(
  value: unknown,
): value is z.infer<typeof AuthTokensSchema> {
  return AuthTokensSchema.safeParse(value).success;
}

export function isStoredOrgContext(
  value: unknown,
): value is z.infer<typeof OrgContextSchema> {
  return OrgContextSchema.safeParse(value).success;
}

function removeLegacyStoredAuthTokens(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage?.removeItem(AUTH_TOKEN_STORAGE_KEY);
  window.sessionStorage?.removeItem(AUTH_TOKEN_STORAGE_KEY);
}

function readWindowStorageValue(storageLike: Storage | undefined, key: string): unknown | null {
  if (!storageLike) {
    return null;
  }

  const item = storageLike.getItem(key);
  if (!item) {
    return null;
  }

  const parsed: unknown = JSON.parse(item);
  return parsed;
}

function getStorageReadOrder(key: string): Storage[] {
  if (typeof window === "undefined") {
    return [];
  }

  if (key === AUTH_TOKEN_STORAGE_KEY) {
    removeLegacyStoredAuthTokens();
    return [];
  }

  return [window.localStorage, window.sessionStorage].filter(
    (storageLike): storageLike is Storage => Boolean(storageLike),
  );
}

// Local storage utilities
export const storage = {
  set<T>(key: string, value: T): void {
    try {
      if (key === AUTH_TOKEN_STORAGE_KEY) {
        removeLegacyStoredAuthTokens();
        return;
      }

      const storageTarget =
        typeof window !== "undefined" ? window.localStorage : localStorage;
      storageTarget.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn("Failed to save to localStorage:", error);
    }
  },

  getUnknown(key: string): unknown | null {
    try {
      for (const storageLike of getStorageReadOrder(key)) {
        const value = readWindowStorageValue(storageLike, key);
        if (value !== null) {
          return value;
        }
      }

      return null;
    } catch (error) {
      console.warn("Failed to read from storage:", error);
      return null;
    }
  },

  get<T>(
    key: string,
    defaultValue: T,
    validator?: StorageValidator<T>,
  ): T {
    const parsed = this.getUnknown(key);
    return validateStoredValue(key, parsed, defaultValue, validator);
  },

  remove(key: string): void {
    try {
      if (key === AUTH_TOKEN_STORAGE_KEY && typeof window !== "undefined") {
        clearInMemoryAuthTokens();
        removeLegacyStoredAuthTokens();
        return;
      }

      localStorage.removeItem(key);
    } catch (error) {
      console.warn("Failed to remove from localStorage:", error);
    }
  },

  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.warn("Failed to clear localStorage:", error);
    }
  },

  getToken(): string | null {
    try {
      removeLegacyStoredAuthTokens();
      return getInMemoryAuthTokens()?.accessToken ?? null;
    } catch (error) {
      console.warn("Failed to get token from storage:", error);
      return null;
    }
  },
};

export function getStoredAuthTokens(): z.infer<typeof AuthTokensSchema> | null {
  removeLegacyStoredAuthTokens();
  return normalizeAuthTokens(getInMemoryAuthTokens());
}

export function getStoredOrgContext(): z.infer<typeof OrgContextSchema> | null {
  const result = OrgContextSchema.safeParse(storage.getUnknown("terramatrix:org-context"));
  return result.success ? result.data : null;
}

export function getStoredUserEmail(): string | null {
  const result = StoredUserSchema.safeParse(storage.getUnknown("terramatrix_user"));
  return result.success ? result.data.email ?? null : null;
}

export function hasAuthoritativePlatformAdminEntitlement(
  user: Pick<User, "id"> | null | undefined,
): boolean {
  if (!user?.id) {
    return false;
  }

  const authState = readPlatformAdminEntitlementSnapshot();
  return (
    authState.platformAdminEntitlement === true &&
    authState.userId === user.id
  );
}

export function canTriageFeedback(
  user: Pick<User, "id"> | null | undefined,
): boolean {
  return hasAuthoritativePlatformAdminEntitlement(user);
}

// Session storage utilities
export const sessionStorage = {
  set<T>(key: string, value: T): void {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn("Failed to save to sessionStorage:", error);
    }
  },

  getUnknown(key: string): unknown | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      return readWindowStorageValue(window.sessionStorage, key);
    } catch (error) {
      console.warn("Failed to read from sessionStorage:", error);
      return null;
    }
  },

  get<T>(
    key: string,
    defaultValue: T,
    validator?: StorageValidator<T>,
  ): T {
    const parsed = this.getUnknown(key);
    return validateStoredValue(key, parsed, defaultValue, validator);
  },

  remove(key: string): void {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.warn("Failed to remove from sessionStorage:", error);
    }
  },

  clear(): void {
    try {
      window.sessionStorage.clear();
    } catch (error) {
      console.warn("Failed to clear sessionStorage:", error);
    }
  },
};

// Array utilities
export function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const newArray = [...array];
  const item = newArray.splice(from, 1)[0];
  newArray.splice(to, 0, item);
  return newArray;
}

export function arrayRemove<T>(array: T[], index: number): T[] {
  return array.filter((_, i) => i !== index);
}

export function arrayInsert<T>(array: T[], index: number, item: T): T[] {
  const newArray = [...array];
  newArray.splice(index, 0, item);
  return newArray;
}

export function arrayReplace<T>(array: T[], index: number, item: T): T[] {
  const newArray = [...array];
  newArray[index] = item;
  return newArray;
}

// Number utilities
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function lerp(start: number, end: number, factor: number): number {
  return start + (end - start) * factor;
}

export function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

// Color utilities
export function hexToRgb(
  hex: string,
): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("")}`;
}

// Date utilities
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

// Keyboard utilities
export function isModifierKey(event: KeyboardEvent): boolean {
  return event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;
}

export function getModifierKeys(event: KeyboardEvent): string[] {
  const modifiers: string[] = [];
  if (event.ctrlKey) modifiers.push("ctrl");
  if (event.metaKey) modifiers.push("meta");
  if (event.altKey) modifiers.push("alt");
  if (event.shiftKey) modifiers.push("shift");
  return modifiers;
}

// Browser utilities
export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  } else {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    return new Promise((resolve, reject) => {
      if (document.execCommand("copy")) {
        resolve();
      } else {
        reject(new Error("Failed to copy to clipboard"));
      }
      document.body.removeChild(textArea);
    });
  }
}

// Performance utilities
export function measurePerformance<T>(fn: () => T, label?: string): T {
  const start = performance.now();
  const result = fn();
  const end = performance.now();

  if (label) {
    console.log(`${label} took ${end - start} milliseconds`);
  }

  return result;
}

// Error handling utilities
export function createErrorWithCode(
  message: string,
  code: string,
): Error & { code: string } {
  return Object.assign(new Error(message), { code });
}

export function isErrorWithCode(
  error: unknown,
  code: string,
): error is Error & { code: string } {
  return error instanceof Error && "code" in error && error.code === code;
}

// Simple logger for frontend development
export const logger = {
  debug: (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.debug("[DEBUG]", ...args);
    }
  },

  info: (...args: unknown[]) => {
    // Only log info messages in production or when explicitly debugging
    if (import.meta.env.PROD || import.meta.env.VITE_DEBUG_LOGGING === "true") {
      console.info("[INFO]", ...args);
    }
  },

  warn: (...args: unknown[]) => {
    console.warn("[WARN]", ...args);
  },

  error: (...args: unknown[]) => {
    console.error("[ERROR]", ...args);
  },

  log: (...args: unknown[]) => {
    console.log("[LOG]", ...args);
  },
};
